import { useState, type ChangeEvent } from "react";
import { useT } from "@transifex/react";
import CharacterCount from "../../basic/CharacterCount";
import InputWithActions from "../../basic/inputWithActions/InputWithActions";
import IconTextRow from "../../basic/iconTextRow/IconTextRow";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import ConsentCheckbox from "./ConsentCheckbox";
import ClassVisibilitySelector from "./ClassVisibilitySelector";
import type { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import { SchoolClassVisibilities } from "../../enums/schoolClasses";
import { MAX_NAME_LENGTH } from "../../constants/LimitConstants";
import { isPrivateSchoolClass, validateClassName } from "../../utils/schoolClassUtils";

interface InlineCreateClassFormProps {
    name: string;
    hasConsent: boolean;
    existingClassNames: string[];
    onNameChange: (name: string) => void;
    onConsentToggle: () => void;
    // X icon — discards form data.
    onClose: () => void;
    // Back-row above the form — collapses the form view but keeps any pending pick.
    onCollapse: () => void;

    // When omitted, the form is private-only (no selector shown, consent always required).
    // When provided, a public/private selector is rendered and consent is gated on private.
    visibility?: SchoolClassVisibilities;
    onVisibilityChange?: (visibility: SchoolClassVisibilities) => void;
    classActivityCategory?: ClassActivityCategory;
    isSubmitting?: boolean;
}

const InlineCreateClassForm = (props: InlineCreateClassFormProps) => {
    const t = useT();

    const t_newPrivateClass = t("New private class", { _tags: "SchoolClasses, InlineCreateClassForm" });
    const t_newPublicClass = t("New public class", { _tags: "SchoolClasses, InlineCreateClassForm" });
    const t_nameLabel = t("Class name:", { _tags: "SchoolClasses, InlineCreateClassForm" });
    const t_backToClassSelection = t("Select existing class", { _tags: "SchoolClasses, InlineCreateClassForm" });
    const t_sr_closeForm = t("Close form", { _tags: "SchoolClasses, InlineCreateClassForm, ScreenReaderOnly" });
    const t_classType = t("Class type", { _tags: "SchoolClasses, InlineCreateClassForm" });
    const t_duplicateWarning = t("A class with this name already exists. Tip: Add the year.", {
        _tags: "SchoolClasses, InlineCreateClassForm",
    });
    const t_unsupportedCharacter = t(
        "The class name contains unsupported special characters. Please use only letters, numbers, and spaces.",
        { _tags: "SchoolClasses, InlineCreateClassForm" }
    );
    const t_consent = t(
        "I confirm that personal data of students (e.g. name and performance data) may be processed for this private class and, if applicable, linked to phase6 accounts.",
        { _tags: "SchoolClasses, InlineCreateClassForm" }
    );

    const [isEditing, setIsEditing] = useState(false);

    const handleStopEditing = () => setIsEditing(false);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        props.onNameChange(e.target.value);
    };

    const validationError = validateClassName(props.name, props.existingClassNames);
    const getErrorMessage = (): string => {
        switch (validationError) {
            case "unsupported":
                return t_unsupportedCharacter;
            case "duplicate":
                return t_duplicateWarning;
            default:
                return "";
        }
    };
    const errorMessage = props.isSubmitting ? "" : getErrorMessage();

    const hasVisibilitySelector = props.visibility !== undefined && !!props.onVisibilityChange;
    const isPrivate = !hasVisibilitySelector || isPrivateSchoolClass(props.visibility);
    const formHeader = isPrivate ? t_newPrivateClass : t_newPublicClass;

    const renderBackRow = () => (
        <button
            type="button"
            onClick={props.onCollapse}
            className="flex items-center gap-xs w-full border-none p-no text-l text-p6-grey-dark cursor-pointer bg-transparent"
        >
            <PhaseSixIcon name="chevron-left" className="text-xxl" />
            <span>{t_backToClassSelection}</span>
        </button>
    );

    const renderFormCard = () => (
        <div className="flex flex-col gap-s border border-p6-grey-dark rounded-small-container px-s py-s">
            <div className="flex items-center justify-between">
                <span className="text-l font-semibold text-p6-grey-dark">{formHeader}</span>
                <button
                    type="button"
                    aria-label={t_sr_closeForm}
                    onClick={props.onClose}
                    className="bg-transparent border-none cursor-pointer p-no"
                >
                    <PhaseSixIcon name="close" className="text-xxl text-p6-grey-dark" />
                </button>
            </div>

            <hr className="border-t border-p6-grey-light m-no" />

            <div className="flex flex-col gap-xxs">
                <div className="flex items-center gap-xs flex-wrap">
                    <span className="text-l text-p6-grey-dark shrink-0">{t_nameLabel}</span>
                    <div
                        data-testid="inline-create-class-name"
                        className="flex-1"
                    >
                        <InputWithActions
                            data-testid="inline-create-class-name-input"
                            currentText={props.name}
                            isEditing={isEditing}
                            setIsEditing={setIsEditing}
                            maxLength={MAX_NAME_LENGTH}
                            onChange={handleInputChange}
                            onSave={handleStopEditing}
                            onDiscard={handleStopEditing}
                        />
                    </div>
                </div>
                {isEditing && (
                    <CharacterCount
                        currentLength={props.name.length}
                        maxLength={MAX_NAME_LENGTH}
                    />
                )}
            </div>

            {hasVisibilitySelector && (
                <div className="flex flex-col gap-xs">
                    <span className="text-l text-p6-grey-dark">{t_classType}</span>
                    <ClassVisibilitySelector
                        selectedVisibility={props.visibility ?? SchoolClassVisibilities.PUBLIC}
                        onVisibilityChange={props.onVisibilityChange!}
                        classActivityCategory={props.classActivityCategory}
                    />
                </div>
            )}
        </div>
    );

    return (
        <div className="flex flex-col gap-s w-full">
            {renderBackRow()}
            {renderFormCard()}
            {!!errorMessage && (
                <IconTextRow
                    icon="alert"
                    text={errorMessage}
                    iconClassName="text-xxl text-p6-red"
                    textClassName="text-l text-p6-red"
                />
            )}
            {isPrivate && (
                <ConsentCheckbox
                    isChecked={props.hasConsent}
                    onChange={props.onConsentToggle}
                    label={t_consent}
                    classActivityCategory={props.classActivityCategory}
                />
            )}
        </div>
    );
};

export default InlineCreateClassForm;
